import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { settingsService, PortalSetting } from '../services/settings.service';

export type ThemeType = 'original' | 'default' | 'dark' | 'ocean' | 'slate' | 'rose' | 'forest';
export type DesignVersion = 'v1' | 'v2';

interface SettingsContextType {
  settings: PortalSetting[];
  isAiChatEnabled: boolean;
  currentTheme: ThemeType;
  setTheme: (theme: ThemeType) => Promise<void>;
  designVersion: DesignVersion;
  setDesignVersion: (v: DesignVersion) => Promise<void>;
  isLoading: boolean;
  refreshSettings: () => Promise<void>;
}

const SettingsContext = createContext<SettingsContextType>({
  settings: [],
  isAiChatEnabled: true,
  currentTheme: 'original',
  setTheme: async () => {},
  designVersion: 'v1',
  setDesignVersion: async () => {},
  isLoading: true,
  refreshSettings: async () => {},
});

function applyTheme(theme: ThemeType) {
  document.body.dataset.theme = theme;
}

function applyDesignVersion(v: DesignVersion) {
  document.body.dataset.designVersion = v;
}

export const SettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<PortalSetting[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const refreshSettings = useCallback(async () => {
    try {
      const data = await settingsService.getAll();
      setSettings(data);
      const savedTheme = (data.find((s) => s.key === 'theme')?.value ?? 'original') as ThemeType;
      applyTheme(savedTheme);
      const savedDesignVersion = (data.find((s) => s.key === 'design_version')?.value ?? 'v1') as DesignVersion;
      applyDesignVersion(savedDesignVersion);
    } catch {
      // 설정 로딩 실패 시 기본값 유지
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshSettings();
  }, [refreshSettings]);

  const isAiChatEnabled = settings.find((s) => s.key === 'ai_chat_enabled')?.value !== 'false';
  const currentTheme = (settings.find((s) => s.key === 'theme')?.value ?? 'original') as ThemeType;
  const designVersion = (settings.find((s) => s.key === 'design_version')?.value ?? 'v1') as DesignVersion;

  const setTheme = async (theme: ThemeType) => {
    applyTheme(theme);
    await settingsService.update('theme', theme);
    await refreshSettings();
  };

  const setDesignVersion = async (v: DesignVersion) => {
    applyDesignVersion(v);
    await settingsService.update('design_version', v);
    await refreshSettings();
  };

  return (
    <SettingsContext.Provider value={{ settings, isAiChatEnabled, currentTheme, setTheme, designVersion, setDesignVersion, isLoading, refreshSettings }}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => useContext(SettingsContext);
